import { defineComponent as _defineComponent } from 'vue'
import { apolloClient, queries, mutations } from "@/apollo";
import { User, Release, AppMode, StoredTtPage } from "@/CodegenTypes";
import { parseTtt } from "@/helpers/ttt/parseTtt";
import { summariseStoredPage } from "@/helpers/ttt/summariseStoredPage";
import { errorMutationNotification } from "@/utils";
import AccountSelectModal from "@/components/modules/account/AccountSelectModal.vue";
import { TT_DAY_MAPPING } from "@/constants";
import { customMoment } from "@/main";
import { TTDay } from "@/types";
import { getTtValidDayOptions } from "@/helpers/getTtValidDayOptions";
import { useTranslation } from "@/store/translation";
import { useDialogStore } from "@/store/dialogs";
import { computed, getCurrentInstance, onMounted, ref, watch } from "vue";
import { useQuery } from "@vue/apollo-composable";
import { useRoute } from "vue-router/composables";

type RouteQueryValue = string | (string | null)[] | null | undefined;


export default /*#__PURE__*/_defineComponent({
  __name: 'TTUpload',
  setup(__props) {

const route = useRoute();
const vm = getCurrentInstance()?.proxy as any;
const { $t } = useTranslation();
const t = (text: string) => $t(text);

const showAccountSelectionModal = ref(false);
const releases = ref<null | Release[]>(null);
const isSubmitting = ref(false);
const selectedDayIndex = ref(0);
const resetFileUploadKey = ref(Symbol());

const selectedAccountId = computed(() => getRouteQueryValue(route.query.userId));
const releasesVariables = computed(() => {
  return {
    appMode: AppMode.Tt,
  };
});
const userVariables = computed(() => {
  return {
    userId: selectedAccountId.value,
  };
});
const storedTtPagesVariables = computed(() => {
  return {
    storedPageType: "upload",
    includeDownlines: true,
    userId: selectedAccountId.value,
  };
});
const {
  result: releasesResult,
  loading: releasesLoading,
  refetch: refetchReleases,
} = useQuery<{ releases: Release[] }>(queries.releases, releasesVariables, {
  fetchPolicy: "cache-and-network",
});
const {
  result: userResult,
  loading: userLoading,
  refetch: refetchUser,
} = useQuery<{ user: User }>(queries.userWithCreditSummary, userVariables, {
  enabled: computed(() => !!selectedAccountId.value),
  fetchPolicy: "cache-and-network",
});
const {
  result: storedTtPagesResult,
  loading: storedTtPagesLoading,
  refetch: refetchStoredTtPages,
} = useQuery<{ user: { storedTtPages: StoredTtPage[] } }>(
  queries.userStoredTtPages,
  storedTtPagesVariables,
  {
    enabled: computed(() => !!selectedAccountId.value),
    fetchPolicy: "cache-and-network",
  }
);

watch(
  releasesResult,
  (result) => {
    if (result?.releases) {
      releases.value = result.releases;
    }
  },
  { immediate: true }
);

const user = computed(() => userResult.value?.user ?? null);
const storedTtPages = computed(() => storedTtPagesResult.value?.user?.storedTtPages ?? null);
const apolloLoading = computed(
  () => releasesLoading.value || userLoading.value || storedTtPagesLoading.value
);
const openReleases = computed(() =>
  releases.value?.filter((release) => release.windowIsOpen)
);
const validDayOptions = computed(() => {
  if (!releases.value) {
    return [];
  }
  return getTtValidDayOptions(releases.value);
});
const dayOptions = computed(() => {
  const OPTIONS = [
    { label: "1=<Mon>", value: 1, defaultPriority: false },
    { label: "4=<Thu>", value: 4, defaultPriority: false },
    { label: "5=<Mon,Fri>", value: 5, defaultPriority: false },
  ];

  return OPTIONS.filter((o) => validDayOptions.value.includes(o.value));
});
const selectedDay = computed<TTDay>({
  get() {
    if (releases.value) {
      const selectedOption =
        dayOptions.value[selectedDayIndex.value] ?? dayOptions.value[0];
      return (selectedOption?.value ?? 5) as TTDay;
    }
    return 5;
  },
  set(val) {
    const index = dayOptions.value.findIndex((opt) => opt.value === val);
    selectedDayIndex.value = index === -1 ? 0 : index;
  },
});
const selectedReleases = computed(() => {
  const days = TT_DAY_MAPPING[selectedDay.value];
  return releases.value?.filter((release) => days.includes(release.releaseDay));
});
const summarisedUploads = computed(() =>
  storedTtPages.value?.map((storedPage) => ({
    ...storedPage,
    summary: summariseStoredPage(storedPage),
    warnings: {
      noContainUserId:
        !!user.value &&
        !storedPage.uploadFileName?.toLowerCase().includes(user.value.userId),
      fileNameExists:
        storedTtPages.value &&
        storedTtPages.value.filter(
          (page) => page.uploadFileName === storedPage.uploadFileName
        ).length > 1,
    },
  }))
);

onMounted(async () => {
  const priorityIndex = dayOptions.value.findIndex((opt) => opt.defaultPriority);
  selectedDayIndex.value = priorityIndex === -1 ? 0 : priorityIndex;

  const releaseResult = await apolloClient.query<{ releases: Release[] }>({
    query: queries.releases,
    variables: releasesVariables.value,
  });
  releases.value = releaseResult.data.releases;
});

function getRouteQueryValue(query: RouteQueryValue) {
  return Array.isArray(query) ? query[0] : query;
}

function handleFileUpload({ files }: { files: File[] }) {
  const file = files[0];
  const reader = new FileReader();
  reader.addEventListener("load", async (event) => {
    const line = event.target?.result;

    if (!selectedReleases.value || !selectedAccountId.value) {
      throw new Error("Missing variables");
    }

    try {
      const parsedTtts = parseTtt(line as string, selectedDay.value);
      if (parsedTtts.length === 0) {
        throw new Error("No valid entries found");
      }
      const firstParsedTtt = parsedTtts[0];
      await apolloClient.mutate({
        mutation: mutations.saveTtEntry,
        variables: {
          page: {
            page: firstParsedTtt.page,
            entries: parsedTtts.map((parsedTtt) => ({
              day: selectedDay.value,
              number1: parsedTtt.numbers[0],
              number2: parsedTtt.numbers[1],
              number3: parsedTtt.numbers[2],
              number4: parsedTtt.numbers[3],
              number5: parsedTtt.numbers[4],
              number6: parsedTtt.numbers[5],
              number7: parsedTtt.numbers[6],
              number8: parsedTtt.numbers[7],
              number9: parsedTtt.numbers[8],
              number10: parsedTtt.numbers[9],
              unit: parsedTtt.unit,
              mode: parsedTtt.mode,
            })),
            uploadMeta: {
              fileName: file.name,
              fileSize: file.size,
            },
            userId: selectedAccountId.value,
            storedPageType: "upload",
          },
        },
      });
      resetFileUploadKey.value = Symbol();
      void reloadAll();

      useDialogStore().addDialog({
        text: "File uploaded successfully! You can see your upload below. Click on green load button to confirm.",
      });
    } catch (err) {
      errorMutationNotification(err);
    }
  });
  if (file) {
    reader.readAsText(file);
  }
}

function formatDatetime(date: Date) {
  return customMoment(date).format("DD MMM YYYY hh:mm:ss A");
}

async function submitFile(index: number) {
  const targetUpload = storedTtPages.value?.[index];
  if (!targetUpload) {
    return;
  }
  try {
    isSubmitting.value = true;

    await apolloClient.mutate({
      mutation: mutations.insertTtEntry,
      variables: {
        page: {
          page: targetUpload.name,
          userId: selectedAccountId.value,
          entries: targetUpload.entries.map((entry) => {
            const numbers = entry.numbersText.split("-");
            return {
              number1: numbers[0],
              number2: numbers[1],
              number3: numbers[2],
              number4: numbers[3],
              number5: numbers[4],
              number6: numbers[5],
              number7: numbers[6],
              number8: numbers[7],
              number9: numbers[8],
              number10: numbers[9],
              day: entry.day,
              unit: entry.unit,
              mode: entry.mode,
            };
          }),
          entrySource: "upload",
        },
      },
    });
    void reloadAll();
    useDialogStore().addDialog({
      text: "Upload successfully loaded into bet list.",
    });
    await handleDelete(targetUpload.id, false);
  } catch (err) {
    errorMutationNotification(err);
  } finally {
    isSubmitting.value = false;
  }
}

async function handleDelete(pageId: string, doPrompt = true) {
  if (!selectedAccountId.value) {
    vm.$toast.add({
      severity: "error",
      summary: "Error",
      detail: "No account selected",
      life: 3000,
    });
    return;
  }

  const performDelete = async () => {
    try {
      isSubmitting.value = true;
      await apolloClient.mutate({
        mutation: mutations.deleteSavedTtPage,
        variables: {
          id: pageId,
        },
      });
      void reloadAll();
      if (doPrompt) {
        vm.$toast.add({
          severity: "success",
          summary: "Success",
          detail: "Upload deleted.",
          life: 3000,
        });
      }
    } catch (err) {
      errorMutationNotification(err);
    } finally {
      isSubmitting.value = false;
    }
  };

  if (doPrompt) {
    vm.$confirm.require({
      header: "Confirm?",
      message: "Are you sure to delete the upload?",
      icon: "pi pi-exclamation-triangle",
      accept: performDelete,
      reject: () => {
        // Optional: Handle rejection
      },
    });
  } else {
    await performDelete();
  }
}

async function reloadAll() {
  await Promise.all([
    refetchReleases(releasesVariables.value),
    selectedAccountId.value ? refetchUser(userVariables.value) : Promise.resolve(),
    selectedAccountId.value
      ? refetchStoredTtPages(storedTtPagesVariables.value)
      : Promise.resolve(),
  ]);
}

return { __sfc: true,route, vm, $t, t, showAccountSelectionModal, releases, isSubmitting, selectedDayIndex, resetFileUploadKey, selectedAccountId, releasesVariables, userVariables, storedTtPagesVariables, releasesResult, releasesLoading, refetchReleases, userResult, userLoading, refetchUser, storedTtPagesResult, storedTtPagesLoading, refetchStoredTtPages, user, storedTtPages, apolloLoading, openReleases, validDayOptions, dayOptions, selectedDay, selectedReleases, summarisedUploads, getRouteQueryValue, handleFileUpload, formatDatetime, submitFile, handleDelete, reloadAll, AccountSelectModal }
}

})